import { OptionsFill24Icon } from "@vygruppen/spor-icon-react";
import { SharedGridStyleBaseColor } from "features/CenterContent/shared/MainContentGrid";
import { Itinerary } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/Itinerary";
import { VehicleCancellation } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/VehicleCancellation/VehicleCancellation";
import { VehicleUndoCancellation } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/VehicleCancellation/VehicleUndoCancellation";
import { VehiclePhoneNumber } from "features/CenterContent/VehicleDetails/shared/VehiclePhoneNumber";
import { useMemo } from "react";
import { ExpandableInfoMessage } from "shared/components/feedback/InfoMessage/ExpandableInfoMessage/ExpandableInfoMessage";
import { NoDataMessage } from "shared/components/feedback/NoDataMessage/NoDataMessage";
import { Module } from "shared/components/Module";
import { BusGps } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/BusGps/BusGps";
import OperationalLog from "features/CenterContent/VehicleDetails/shared/OperationalLog";
import {
  Box,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
  VStack,
} from "shared/components/Spor";
import {
  AlternativeTransportItinerary,
  AlternativeTransportItineraryStop,
  AlternativeTransportVehicle,
  StopReference,
} from "shared/types/alternativeTransport";
import styled from "styled-components";
import { IncidentsAndStopsPanels } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/IncidentsAndStopsPanels";

const BusModule = styled(Module).attrs({ size: "lg" })`
  height: 100%;
  min-height: 0;
  && {
    border: 1px solid ${({ theme }) => theme.colorOutlineSecondary};
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
`;

const KebabMenuContent = styled(PopoverContent)`
  && {
    background-color: ${({ theme }) => theme.colorBackgroundTertiary};
    border: 1px solid ${({ theme }) => theme.colorOutlineCore};
    box-shadow: none;
    border-radius: 12px;
    padding: 6px;
  }
  && && {
    border: none;
    box-shadow: none;
    background: transparent;
    padding: 0;
    min-width: 0;
    width: 100%;
    align-self: stretch;
  }
  && && > * {
    width: 100%;
  }

  &.spor-popover__body {
    width: 100%;
  }

  [data-part="arrow"] {
    display: none;
  }

  button {
    display: flex;
    align-items: center;
    width: 100% !important;
    max-width: 100% !important;
    justify-content: flex-start;
    white-space: nowrap;
    font-weight: 400 !important;
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
    border-radius: 9px;
    color: ${({ theme }) => theme.colorTextMain} !important;
  }

  button > * {
    white-space: nowrap;
    font-weight: 400 !important;
  }

  button:focus,
  button:focus-visible {
    box-shadow: none !important;
    outline: none !important;
  }

  button svg {
    color: ${({ theme }) => theme.colorTextMain};
  }

  button:hover {
    background: ${({ theme }) => theme.colorTextMain}14 !important;
  }

  button::after {
    content: "→";
    margin-left: auto;
    padding-left: 12px;
    font-size: 18px;
    line-height: 1;
  }
`;

const KebabOnly = styled.div`
  @container altTransportPage (width >= 1696px) {
    display: none;
  }
`;

const InlineActions = styled.div`
  display: none;

  @container altTransportPage (width >= 1696px) {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  button {
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
  }
`;

const HorizontalScroll = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-x: auto;
  overflow-y: hidden;
`;

const ScrollContent = styled.div`
  flex: 1;
  min-height: 0;
  min-width: 809px;
  display: flex;
  flex-direction: column;
  gap: 9px;

  @container altTransportPage (width < 1696px) {
    min-width: 0;
  }
`;

const ModuleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(400px, 812px));
  gap: 9px;

  @container altTransportPage (width < 1696px) {
    grid-template-columns: 1fr;
  }
`;

const TopModuleGrid = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
`;

const ScrollModuleGrid = styled(ModuleGrid)`
  align-items: start;
  padding-bottom: 50px;
  column-gap: 0;
`;

const LeftScrollModule = styled(Module)`
  @container altTransportPage (width >= 1696px) {
    && {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
  }
`;
const RightScrollColumn = styled(VStack)`
  @container altTransportPage (width >= 1696px) {
    && > * {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
  }
`;

interface AlternativeTransportLeftPanelProps {
  vehicle: AlternativeTransportVehicle;
}

export const AlternativeTransportLeftPanel = ({
  vehicle,
}: AlternativeTransportLeftPanelProps) => {
  const trainNumberTurnicate =
    vehicle.trainIds.join(", ").length > 16
      ? `${vehicle.trainIds.join(", ").substring(0, 16)}...`
      : vehicle.trainIds.join(", ");

  const getDistributionStopPlace = (
    itinerary: AlternativeTransportItinerary | null,
  ) =>
    itinerary
      ? [...itinerary.stops, ...(itinerary.returnStops ?? [])]
          .filter((stop) => !!stop.stopReference.nsrStopPlace)
          .map((stop) => stop.stopReference.nsrStopPlace)
          .join(",")
      : "";

  const distributionStopReferences: Array<StopReference> = useMemo(() => {
    const stops = vehicle?.itinerary?.stops ?? [];
    const returnStops = vehicle?.itinerary?.returnStops ?? [];
    return [...stops, ...returnStops].map(
      (value: AlternativeTransportItineraryStop) => value.stopReference,
    );
  }, [vehicle]);

  const distributionStopPlaces = useMemo(
    () => getDistributionStopPlace(vehicle.itinerary),
    [vehicle],
  );

  const shiftId = vehicle.workShiftPlan?.shiftId;
  const titlePrefix = `Avgang: ${shiftId ? `${shiftId}-` : ""}${trainNumberTurnicate}${
    trainNumberTurnicate && !shiftId ? " Operative" : ""
  }`;

  const title = (
    <>
      {titlePrefix}
      {vehicle.isPartOfShuttlePlan && (
        <Box as="span" color="text.subtle">
          {" "}
          Pendel
        </Box>
      )}
    </>
  );

  return (
    <BusModule
      title={title}
      action1={
        <>
          <KebabOnly>
            <Popover positioning={{ placement: "bottom-end" }}>
              <PopoverTrigger>
                <IconButton
                  variant="ghost"
                  size="sm"
                  aria-label="Flere valg"
                  icon={<OptionsFill24Icon />}
                />
              </PopoverTrigger>
              <KebabMenuContent>
                <VStack align="stretch" gap="1" w="100%">
                  <VehiclePhoneNumber
                    phoneNumberUpdate={vehicle.driverPhoneNumber}
                    workShift={vehicle.workShiftPlan}
                    id={vehicle.id}
                    operatorUpdate={{
                      operatorName: vehicle.operatorName,
                      operatorId: vehicle.operatorId,
                    }}
                  />
                  <VehicleCancellation vehicle={vehicle} />
                  <VehicleUndoCancellation vehicle={vehicle} />
                </VStack>
              </KebabMenuContent>
            </Popover>
          </KebabOnly>
          <InlineActions>
            <VehiclePhoneNumber
              phoneNumberUpdate={vehicle.driverPhoneNumber}
              workShift={vehicle.workShiftPlan}
              id={vehicle.id}
              operatorUpdate={{
                operatorName: vehicle.operatorName,
                operatorId: vehicle.operatorId,
              }}
            />
          </InlineActions>
        </>
      }
    >
      <Box flexShrink="0" mb="9px">
        <Itinerary alternativeTransport={vehicle} />
      </Box>
      <HorizontalScroll>
        <ScrollContent>
          <TopModuleGrid>
            <Module title="Info fra Brudd">
              {vehicle.notes.length > 0 ? (
                <div style={{ width: "100%", padding: "0.75rem" }}>
                  <ExpandableInfoMessage
                    semantics="info"
                    title="Info fra Brudd"
                  >
                    {vehicle.notes.map((note) => (
                      <Text key={note}>{note}</Text>
                    ))}
                  </ExpandableInfoMessage>
                </div>
              ) : (
                <NoDataMessage
                  panelStyle={SharedGridStyleBaseColor}
                  reason="Det finnes ikke hendelse denne bussen"
                />
              )}
            </Module>
          </TopModuleGrid>
          <Box
            flex="1"
            minH="0"
            overflowY="auto"
            overflowX="hidden"
            overscrollBehavior="none"
          >
            <ScrollModuleGrid>
              <LeftScrollModule title="Trasé">
                <IncidentsAndStopsPanels
                  vehicle={vehicle}
                  distributionStopReferences={distributionStopReferences}
                />
              </LeftScrollModule>
              <RightScrollColumn gap="9px" align="stretch">
                <Module title="Avgangslogg">
                  <OperationalLog
                    vehicleId={vehicle.id}
                    logs={vehicle.logs}
                    distributionStopPlaces={distributionStopPlaces}
                  />
                </Module>
                <Module title="Gps-data">
                  <BusGps vehicle={vehicle} />
                </Module>
              </RightScrollColumn>
            </ScrollModuleGrid>
          </Box>
        </ScrollContent>
      </HorizontalScroll>
    </BusModule>
  );
};

export default AlternativeTransportLeftPanel;
